import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'

const accent = colors.PRIMARY
const background = colors.SECONDARY_300
const foreground = colors.NEUTRAL_0

/**
 * The house grid look as plain values: the ONE source for the AG Grid theme
 * (`theme.ts`) and for grids that draw the same look without AG Grid (the
 * registration grid, `pages/registration/components/DivisionGrid`). Nothing
 * here imports `ag-grid-community`, so a grid that only needs the look does not
 * pull AG Grid into its bundle.
 *
 * Quartz derives a few values instead of `theme.ts` setting them: the
 * selected-row tint, the checkbox colors, the focus ring and the popup shadow.
 * They are written out below as the exact CSS Quartz computes for this palette
 * (ag-grid-community 36, `themeQuartz` + `colorSchemeDark`), and `theme.ts`
 * passes them back, so both grids share one number.
 *
 * One deliberate departure: the checked tick is `PRIMARY_BUTTON_CONTENT.icon`,
 * the tenant-aware content color for a `colors.PRIMARY` fill (dark on
 * SportNinja's amber, white on Canlan's blue). Quartz paints it in the grid
 * background, which reads dark-on-blue on Canlan.
 */
export const DATA_GRID_TOKENS = {
  /** Tenant-aware: sort arrows, active filter, checkbox fill, selection tint, focus ring. */
  accent,
  /** The row surface and the header, one step above the page. */
  background,
  foreground,
  text: foreground,
  headerText: foreground,
  /** A column-group header, stepped back from the column headers. */
  groupHeaderText: colors.NEUTRAL_50,
  /** A "no rows" message. */
  overlayText: colors.NEUTRAL_70,
  /** Zebra: AG Grid's second row (`.ag-row-odd`) and every other one after it. */
  oddRowBackground: colors.SECONDARY_400,
  rowHover: colors.SECONDARY_200,
  pinnedRowBackground: colors.SECONDARY_200,
  borderColor: colors.SECONDARY_100,
  /** Between rows and under the header: hairlines, not gutters. */
  rowBorderColor: colors.SECONDARY_200,
  /** The frame around a grid. */
  wrapperBorderColor: colors.SECONDARY_100,
  borderWidth: 1,
  radius: 8,
  fontSize: 14,
  cellHorizontalPadding: 14,
  iconSize: 16,
  /** A sortable column's icon while it is not sorted; a filter icon at rest. */
  sortIdle: colors.NEUTRAL_100,
  selectedRowBackground: `color-mix(in srgb, transparent, ${accent} 20%)`,
  focusShadow: `0 0 0 3px color-mix(in srgb, transparent, ${accent} 50%)`,
  popupShadow: `0 0 20px ${colors.BLACK}AA`,
  checkbox: {
    radius: 4,
    uncheckedBackground: background,
    uncheckedBorder: `color-mix(in srgb, ${background}, ${foreground} 40%)`,
    checkedBackground: accent,
    checkedShape: PRIMARY_BUTTON_CONTENT.icon,
    indeterminateBackground: `color-mix(in srgb, ${background}, ${foreground} 30%)`,
    indeterminateShape: background,
  },
} as const
